import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { toast } from "react-toastify";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { TCard, TCart, TPass } from "src/shared/types/globalStotre.types";
import {
  CheckoutFormData,
  CheckoutFormSubmitData,
} from "src/components/reusable/components/CheckoutForm";
import { useGroupInviteFlow } from "../groupInviteFlow/useGroupInviteFlow";
import { dispatchCustomEvent, throttleTime } from "src/utils/utils";
import { useHomePageFlow } from "../homepageFlow/useHomePageFlow";
import { useManageCartErrorModal } from "src/components/feature/PurchaseFlow/ManageCartErrorModal";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { usePassErrorMessageModal } from "./PassErrorMessageModal";
import { usePassFlow } from "./usePassFlow";
import { useAddOnCheckoutErrorModal } from "src/components/reusable/components/GlobalModals/AddOnCheckoutErrorModal";
import { findSelectedAddOnsFromEvents } from "../homepageFlow/utils.homepageFlow";
import { findHeldLocationsFromCart } from "./utils.purchaseFlow";
import { useAdditionalDetails } from "./useAdditionalDetails";
import { useSeatLocationsModal } from "src/components/reusable/components/GlobalModals/SeatLocationsModal";
import { useCheckoutErrorHandler } from "./useCheckoutErrorHandler";
import { useGenericErrorModal } from "src/components/reusable/hooks/useGenericErrorModal";
import { useSendGiftSuccessModal } from "../giftFlow/sendGift/useSendGiftSuccessModal";
import { TCheckoutApiResponse } from "src/shared/types/api.types";

export const checkoutTabs = constants.checkoutTabs;

export function usePurchaseFlow() {
  const router = useRouterExt();
  const { TEAM_GLOBAL_SETTINGS } = useTeamConfig();
  const {
    passes,
    cart,
    oAuthState,
    session,
    events,
    setGlobalState,
    user,
    attribution,
  } = useGlobalStore();
  const {
    handleJoinGroup,
    isGroupInviteFlow,
    setSwafPromptGetStartSuccessMsg,
    setGroupInvitePurchaseSuccessMsg,
  } = useGroupInviteFlow();
  const { handleAddOnPurchaseSuccess, clearAddOnsCart } = useHomePageFlow();
  const { setPassPurchaseSuccessMessage } = usePassFlow();
  const sendGiftSuccessModal = useSendGiftSuccessModal();

  const checkoutTabActive = router.query.tab;
  const urlPassId = Number(router.query.passId) as number;
  const seatLocationsModal = useSeatLocationsModal();
  const {
    getPassesApi,
    getPassByIdApi,
    manageCartApi,
    checkoutApi,
    findAndRetrieveEkataScoreApi,
    cancelCartApi,
  } = useApiRoutes();
  const passErrorMessageModal = usePassErrorMessageModal();
  const manageCartErrorModal = useManageCartErrorModal();
  const addOnCheckoutErrorModal = useAddOnCheckoutErrorModal();
  const { handleAddOnCheckoutError, handlePassCheckoutError } = useCheckoutErrorHandler(); // prettier-ignore
  const { fireErrorModal } = useGenericErrorModal();
  const {
    handleSubmitAdditionalDetails,
    handleCheckAdditionalDetailsRequired,
  } = useAdditionalDetails();

  const switchCheckoutTab = (tab: string) => {
    router.pushSchema({ query: { tab } });
  };

  const onLoadCheckoutPage = async () => {
    try {
      if (!session) return; // returning if session is not available
      if (cart?.products?.length) return; // prevent loading pass if add ons/products are in cart
      if (cart?.reservations?.length) return; // prevent loading pass for paid-reservation checkout

      // pass group enabled handling
      if (TEAM_GLOBAL_SETTINGS?.IS_PASS_GROUP_ENABLED) {
        let passes = (await getPassesApi.get()) as TPass[];
        return setGlobalState({ passes: [...passes] });
      }

      // regular pass handling without pass group enabled
      if (!urlPassId) return;
      let passSelected = passes?.find((p) => p.passId == urlPassId) as TPass;
      if (!passSelected) {
        passSelected = await getPassByIdApi.get({ appendPath: urlPassId });
        setGlobalState({ passes: [...passes, passSelected] });
      }

      if (passSelected?.errorMessage) {
        passErrorMessageModal.set({
          isOpen: true,
          errorMessage: passSelected.errorMessage,
          onClose: async () => {
            router.pushSchema(
              TEAM_GLOBAL_SETTINGS?.DEFAULT_APP_ROUTE || routes.landing
            );
          },
        });
      }
    } catch (error) {
      toast.error(findApiErrorMessage(error));
    }
  };

  const handlePurchaseSuccess = async (
    cartRes: any,
    checkoutRes: TCheckoutApiResponse,
    route?: string
  ) => {
    setGlobalState({ cart: { ...cartRes, isCheckoutSuccess: true } });

    dispatchCustomEvent(constants.customEvents.purchaseSuccess, {
      cartRes,
      checkoutRes,
      passes,
      user,
    });
    await throttleTime(500);

    // if cartRes products length and we have showSeatLocation, then we return with a modal
    if (
      !seatLocationsModal.isOpen &&
      (cartRes as TCart)?.products?.length &&
      (cartRes as TCart)?.products?.some((itm) => itm?.showSeatLocation)
    ) {
      return seatLocationsModal.set({ isOpen: true, cartRes, checkoutRes });
    }

    // if gift purchase, return with a success modal
    if (checkoutRes?.isPassGift) {
      sendGiftSuccessModal.open(checkoutRes);
      return await router.pushSchema(routes.landing);
    }

    // group invite pass purchase success handling
    if (isGroupInviteFlow) {
      await handleJoinGroup();
      setGroupInvitePurchaseSuccessMsg();
      return await router.pushSchema(routes.home);
    }

    // swaf CTA get start pass purchase success handling
    if (oAuthState?.authOrigin == constants.authOrigins.swafPromptGetStart) {
      setSwafPromptGetStartSuccessMsg();
      return await router.pushSchema(routes.my_group);
    }

    // regular pass purchase success handling
    if (cartRes?.passes?.length) {
      setPassPurchaseSuccessMessage();
      return await router.pushSchema(routes.home);
    }

    // paid-reservation purchase success — same surface as pass purchase
    if (cartRes?.reservations?.length) {
      setPassPurchaseSuccessMessage();
      return await router.pushSchema(routes.home);
    }

    // add ons / products purchase success handling
    if (cartRes?.products?.length) {
      await handleAddOnPurchaseSuccess(checkoutRes, route);
    }
  };

  const handleEkataCheck = async (cardDetail?: CheckoutFormData) => {
    let error: any = null;
    let ekataResponse: any = { isValid: false };

    if (!cardDetail) return { ekataResponse: { isValid: true }, error };

    try {
      ekataResponse = await findAndRetrieveEkataScoreApi.post({
        cardDetail: {
          firstName: cardDetail?.firstName.trim() || "",
          lastName: cardDetail?.lastName.trim() || "",
          address: cardDetail?.address.trim(),
          zipCode: cardDetail?.zipCode.trim(),
          city: cardDetail?.city,
          country: cardDetail?.country,
          state: cardDetail?.state,
        },
      });

      if (!ekataResponse?.isValid) fireErrorModal(ekataResponse?.message);
    } catch (err) {
      error = err;
      fireErrorModal(err);
    } finally {
      return { ekataResponse, error };
    }
  };

  const handlePassCheckout = async (payload: CheckoutFormSubmitData) => {
    const { ekataResponse, error } = await handleEkataCheck(payload.cardDetail);
    if (error || !ekataResponse?.isValid) return;

    try {
      // checking if additional details required fields are filled
      if (!handleCheckAdditionalDetailsRequired()) return;

      // capture exchange fields before manageCart overwrites the cart state
      const oldEventCode = cart?.oldEventCode;
      const oldSeatCount = cart?.oldSeatCount;

      // pass purchase and group invite purchase submit handling
      const cartRes = await manageCartApi.post(cart);
      const passesHeld = cartRes?.passes?.every((p) => p?.isHeld) ?? true;
      const reservationsHeld =
        cartRes?.reservations?.every((r) => r?.isHeld) ?? true;

      setGlobalState({ cart: cartRes });

      // ManageCartErrorModal is pass-only (it renders one row per cart pass and
      // offers "continue with the remaining items"). A paid-reservation cart
      // comes back with no `passes` key at all, and a reservation TM couldn't
      // hold leaves nothing to check out — so surface the BE message and stop
      // instead of opening that modal with an undefined pass list.
      if (!reservationsHeld) {
        const failedReservation = cartRes?.reservations?.find((r) => !r?.isHeld);
        return fireErrorModal(
          failedReservation?.errorMessage ||
            "We couldn't reserve your seat for this game. Please try again."
        );
      }

      // Reaching here with `!passesHeld` guarantees `passes` is a non-empty
      // array — `every` on undefined would have short-circuited to `true`.
      if (!passesHeld) {
        const { isConfirmed } = await manageCartErrorModal.execute({
          isOpen: true,
          cartPasses: cartRes.passes,
        });
        if (!isConfirmed) return; // user clicked try again
      }

      const checkoutRes = await checkoutApi.post({
        config: { timeout: 30000 },
        ...cartRes,
        ...payload,
        ...(oldEventCode ? { oldEventCode, oldSeatCount } : {}),
        // Sales-rep attribution, when the fan arrived through a rep link.
        // Spread conditionally so a checkout without one sends no extra keys at
        // all — the BE treats both as optional and does nothing when absent.
        ...(attribution.infoFields.length > 0
          ? { infoFields: attribution.infoFields }
          : {}),
        ...(attribution.repAccountId
          ? { repAccountId: attribution.repAccountId }
          : {}),
      });
      await handlePurchaseSuccess(cartRes, checkoutRes);
      await handleSubmitAdditionalDetails();
    } catch (err) {
      const { is202Error } = await handlePassCheckoutError(err);
      if (is202Error) await router.back();
    }
  };

  // Paid-reservation manage-cart pass. Mirrors manageAddOnCart but for
  // cart.reservations — runs on the checkout page load so the BE can hold
  // the seats and fill in price/serviceFee/additionalFee before the user
  // submits the form. Errors fire the generic error modal (no dedicated
  // failure modal yet, mirror addOn flow if/when needed).
  const manageReservationCart = async () => {
    let error: any = null;
    let cartRes: TCart = cart;
    try {
      cartRes = await manageCartApi.post({
        reservations: cart?.reservations,
        ...(cart?.isExchange ? { isExchange: true } : {}),
      });
      setGlobalState({ cart: { ...cartRes, isExchange: cart?.isExchange, oldEventCode: cart?.oldEventCode, oldSeatCount: cart?.oldSeatCount } });
    } catch (err) {
      error = err;
      fireErrorModal(err);
    }
    return { cartRes, error };
  };

  const manageAddOnCart = async () => {
    let error: any = null;
    let cartRes: TCart = {};
    let cartFailAction: "retry" | "continue" | null = null;

    try {
      let products = findSelectedAddOnsFromEvents(events);
      cartRes = await manageCartApi.post({ products });

      const failedProducts = cartRes?.products?.filter((itm) => !itm?.isHeld);
      const successProducts = cartRes?.products?.filter((itm) => itm.isHeld);
      const isAllSuccess = cartRes?.products?.every((itm) => itm.isHeld);
      const isAllFailed = cartRes?.products?.every((itm) => !itm.isHeld);

      if (!isAllSuccess) {
        cartRes = { ...cartRes, products: successProducts };
        const errorModalRes = await addOnCheckoutErrorModal.execute({
          failedProducts,
          successProducts,
          isAllFailed,
        });
        if (errorModalRes.isConfirmed) cartFailAction = "continue";
        else cartFailAction = "retry";
      }
    } catch (err) {
      error = err;
      fireErrorModal(err);
    } finally {
      setGlobalState({ cart: cartRes });
      return { cartRes, error, cartFailAction };
    }
  };

  const handleAddOnQuickCheckout = async (args: {
    cardDetail?: CheckoutFormData;
    savedCard?: Partial<TCard>;
  }) => {
    const { ekataResponse, error : ekataErr } = await handleEkataCheck(args.cardDetail); // prettier-ignore
    if (ekataErr || !ekataResponse?.isValid) return;

    // checking if additional details required fields are filled
    if (!handleCheckAdditionalDetailsRequired()) return;

    const { cartRes, cartFailAction, error : manageCartErr } = await manageAddOnCart(); // prettier-ignore
    if (manageCartErr) return;
    if (cartFailAction == "retry") {
      handleCancelCart(cartRes);
      return await router.pushSchema(routes.home);
    }

    try {
      const checkoutRes = await checkoutApi.post({
        config: { timeout: 30000 },
        ...cartRes,
        ...args,
      });
      await handlePurchaseSuccess(cartRes, checkoutRes);
      await handleSubmitAdditionalDetails();
    } catch (err) {
      handleCancelCart(cartRes);
      const { is202Error } = await handleAddOnCheckoutError(err);
      if (is202Error) clearAddOnsCart();
    }
  };

  const handleAddOnProceedToCheckout = async () => {
    const { cartRes, cartFailAction, error } = await manageAddOnCart();
    if (error) return;
    if (cartFailAction == "retry") {
      handleCancelCart(cartRes);
      return await router.pushSchema(routes.home);
    }
    return await router.pushSchema({
      pathname: routes.checkout,
      query: { tab: checkoutTabs.checkout },
    });
  };

  const handleAddOnRegularCheckout = async (args: {
    cardDetail?: CheckoutFormData;
    savedCard?: Partial<TCard>;
  }) => {
    const { ekataResponse, error : ekataErr } = await handleEkataCheck(args.cardDetail); // prettier-ignore
    if (ekataErr || !ekataResponse?.isValid) return;

    // checking if additional details required fields are filled
    if (!handleCheckAdditionalDetailsRequired()) return;

    try {
      const checkoutRes = await checkoutApi.post({
        config: { timeout: 30000 },
        ...cart,
        ...args,
      });
      await handlePurchaseSuccess(cart, checkoutRes);
      await handleSubmitAdditionalDetails();
    } catch (err) {
      const { is202Error } = await handleAddOnCheckoutError(err);
      if (is202Error) {
        await router.back();
        clearAddOnsCart();
      }
    }
  };

  const handleCancelCart = async (cart: TCart) => {
    try {
      const locations = findHeldLocationsFromCart(cart);
      const cartId = cart?.cartId;
      const sessionId = cart?.sessionId;
      const isCheckoutSuccess = cart?.isCheckoutSuccess;

      setGlobalState({ cart: { passes: [], products: [], reservations: [] } });
      if (cartId && !isCheckoutSuccess) {
        await cancelCartApi.post({ cartId, locations, sessionId });
      }
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  return {
    onLoadCheckoutPage,
    handlePassCheckout,
    switchCheckoutTab,
    checkoutTabActive,
    handleCancelCart,
    handlePurchaseSuccess,
    handleAddOnQuickCheckout,
    handleAddOnRegularCheckout,
    handleAddOnProceedToCheckout,
    manageReservationCart,
  };
}
